import { useState, useCallback, useRef } from 'react';
import { IPropertyPhoto } from '@stores/Property/types';
import {
  applyWatermarkAndConvertToJpeg,
  flipImage,
  rotateImage,
  FlipDirection,
  RotationDegrees,
  WatermarkPosition
} from '@utils/image';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const watermarkSrc: string = require('@assets/images/Logotipo/Elite Prime_LogoBRANCO.png');

const DEFAULT_WATERMARK_POSITION: WatermarkPosition = 'top-right';
const DEFAULT_WATERMARK_OPACITY = 0.6;
const DEFAULT_WATERMARK_SIZE = 0.4;
const DEFAULT_WATERMARK_CENTER_IN_QUADRANT = true;
const IMAGE_PROCESSING_MAX_WIDTH = 1280;
const IMAGE_PROCESSING_MAX_HEIGHT = 1024;
const IMAGE_PROCESSING_QUALITY = 0.82;
const IMAGE_PROCESSING_CONCURRENCY = 2;

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return [];

  const results = new Array<R>(items.length);
  let currentIndex = 0;
  const workerCount = Math.max(1, Math.min(concurrency, items.length));

  const worker = async () => {
    while (currentIndex < items.length) {
      const index = currentIndex;
      currentIndex += 1;
      results[index] = await mapper(items[index], index);
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

export interface UseImageUploaderProps {
  initialPhotos?: IPropertyPhoto[];
  imovelId?: number;
  onPhotosChange?: (photos: IPropertyPhoto[]) => void;
}

export interface UseImageUploaderReturn {
  photos: IPropertyPhoto[];
  localImages: File[];
  watermarkPositions: Map<number, WatermarkPosition>;
  watermarkOpacities: Map<number, number>;
  watermarkSizes: Map<number, number>;
  setPhotos: React.Dispatch<React.SetStateAction<IPropertyPhoto[]>>;
  handleAddImage: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleDeleteImage: (index: number, uri: string) => void;
  handleSortEnd: (oldIndex: number, newIndex: number) => void;
  handleWatermarkPositionChange: (
    photoId: number,
    position: WatermarkPosition
  ) => Promise<void>;
  handleWatermarkOpacityChange: (
    photoId: number,
    opacity: number
  ) => Promise<void>;
  handleWatermarkSizeChange: (
    photoId: number,
    relativeWidth: number
  ) => Promise<void>;
  handleRotateImage: (
    photoId: number,
    degrees: RotationDegrees
  ) => Promise<void>;
  handleFlipImage: (photoId: number, direction: FlipDirection) => Promise<void>;
}

export const useImageUploader = ({
  initialPhotos = [],
  imovelId = 0,
  onPhotosChange
}: UseImageUploaderProps = {}): UseImageUploaderReturn => {
  const [photos, setPhotos] = useState<IPropertyPhoto[]>(initialPhotos);
  const [localImages, setLocalImages] = useState<File[]>([]);
  const [watermarkPositions, setWatermarkPositions] = useState<
    Map<number, WatermarkPosition>
  >(new Map());
  const [watermarkOpacities, setWatermarkOpacities] = useState<
    Map<number, number>
  >(new Map());
  const [watermarkSizes, setWatermarkSizes] = useState<Map<number, number>>(
    new Map()
  );

  // Armazena os arquivos originais (sem marca d'água) para re-aplicação
  const originalFilesRef = useRef<Map<number, File>>(new Map());

  const handleAddImage = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const fileArray = Array.from(files);

      // Limita processamento concorrente para evitar travamentos no browser.
      const watermarkedFiles = await mapWithConcurrency(
        fileArray,
        IMAGE_PROCESSING_CONCURRENCY,
        (file) =>
          applyWatermarkAndConvertToJpeg(file, watermarkSrc, {
            position: DEFAULT_WATERMARK_POSITION,
            centerInQuadrant: DEFAULT_WATERMARK_CENTER_IN_QUADRANT,
            relativeWidth: DEFAULT_WATERMARK_SIZE,
            opacity: DEFAULT_WATERMARK_OPACITY,
            quality: IMAGE_PROCESSING_QUALITY,
            maxWidth: IMAGE_PROCESSING_MAX_WIDTH,
            maxHeight: IMAGE_PROCESSING_MAX_HEIGHT,
            returnFileName: file.name
          })
      );

      setLocalImages((prev) => [...prev, ...watermarkedFiles]);

      setPhotos((prev) => {
        const existing = prev || [];
        const maxOrder =
          existing.length > 0
            ? Math.max(...existing.map((f) => f.ordem ?? 0))
            : 0;

        const newPhotos = watermarkedFiles.map((watermarkedFile, index) => {
          const originalFile = fileArray[index];
          const photoId = Date.now() + index;

          // Guarda o arquivo original para re-aplicação de marca d'água
          originalFilesRef.current.set(photoId, originalFile);

          const nextOrder = maxOrder + index + 1;
          return {
            id: photoId,
            imovel_id: imovelId,
            uri: originalFile.name,
            ordem: nextOrder,
            active: true,
            legenda: originalFile.name.replace(/\.[^.]+$/, '') + '.jpg',
            file: watermarkedFile
          };
        });

        // Registra posição/opacidade/tamanho padrão para cada nova foto
        setWatermarkPositions((prev) => {
          const next = new Map(prev);
          newPhotos.forEach((p) => next.set(p.id, DEFAULT_WATERMARK_POSITION));
          return next;
        });
        setWatermarkOpacities((prev) => {
          const next = new Map(prev);
          newPhotos.forEach((p) => next.set(p.id, DEFAULT_WATERMARK_OPACITY));
          return next;
        });
        setWatermarkSizes((prev) => {
          const next = new Map(prev);
          newPhotos.forEach((p) => next.set(p.id, DEFAULT_WATERMARK_SIZE));
          return next;
        });

        const updatedPhotos = [...existing, ...newPhotos];
        onPhotosChange?.(updatedPhotos);
        return updatedPhotos;
      });

      e.target.value = '';
    },
    [imovelId, onPhotosChange]
  );

  const handleDeleteImage = useCallback(
    (index: number, uri: string) => {
      setPhotos((prev) => {
        const photoToDelete = prev?.[index];
        if (photoToDelete?.id) {
          originalFilesRef.current.delete(photoToDelete.id);
          setWatermarkPositions((p) => {
            const next = new Map(p);
            next.delete(photoToDelete.id!);
            return next;
          });
          setWatermarkOpacities((p) => {
            const next = new Map(p);
            next.delete(photoToDelete.id!);
            return next;
          });
          setWatermarkSizes((p) => {
            const next = new Map(p);
            next.delete(photoToDelete.id!);
            return next;
          });
        }

        const updatedPhotos = prev ? prev.filter((_, i) => i !== index) : [];
        const reordered = updatedPhotos.map((p, i) => ({ ...p, ordem: i + 1 }));
        onPhotosChange?.(reordered);
        return reordered;
      });

      setLocalImages((prev) => prev.filter((file) => file.name !== uri));
    },
    [onPhotosChange]
  );

  const handleSortEnd = useCallback(
    (oldIndex: number, newIndex: number) => {
      if (oldIndex === newIndex) return;

      setPhotos((prev) => {
        const sorted = [...prev].sort((a, b) => a.ordem - b.ordem);
        const [movedItem] = sorted.splice(oldIndex, 1);
        sorted.splice(newIndex, 0, movedItem);

        const reordered = sorted.map((p, i) => ({ ...p, ordem: i + 1 }));

        onPhotosChange?.(reordered);
        return reordered;
      });
    },
    [onPhotosChange]
  );

  const applyWatermark = useCallback(
    async (
      photoId: number,
      overrides: {
        position?: WatermarkPosition;
        opacity?: number;
        relativeWidth?: number;
      } = {}
    ) => {
      const originalFile = originalFilesRef.current.get(photoId);
      if (!originalFile) return null;

      const position =
        overrides.position ??
        watermarkPositions.get(photoId) ??
        DEFAULT_WATERMARK_POSITION;
      const opacity =
        overrides.opacity ??
        watermarkOpacities.get(photoId) ??
        DEFAULT_WATERMARK_OPACITY;
      const relativeWidth =
        overrides.relativeWidth ??
        watermarkSizes.get(photoId) ??
        DEFAULT_WATERMARK_SIZE;

      return applyWatermarkAndConvertToJpeg(originalFile, watermarkSrc, {
        position,
        centerInQuadrant: DEFAULT_WATERMARK_CENTER_IN_QUADRANT,
        opacity,
        relativeWidth,
        quality: IMAGE_PROCESSING_QUALITY,
        maxWidth: IMAGE_PROCESSING_MAX_WIDTH,
        maxHeight: IMAGE_PROCESSING_MAX_HEIGHT,
        returnFileName: originalFile.name
      });
    },
    [watermarkPositions, watermarkOpacities, watermarkSizes]
  );

  const handleWatermarkPositionChange = useCallback(
    async (photoId: number, position: WatermarkPosition) => {
      const watermarkedFile = await applyWatermark(photoId, { position });
      if (!watermarkedFile) return;

      setWatermarkPositions((prev) => {
        const next = new Map(prev);
        next.set(photoId, position);
        return next;
      });
      setPhotos((prev) => {
        const updated = prev.map((p) =>
          p.id === photoId ? { ...p, file: watermarkedFile } : p
        );
        onPhotosChange?.(updated);
        return updated;
      });
    },
    [applyWatermark, onPhotosChange]
  );

  const handleWatermarkOpacityChange = useCallback(
    async (photoId: number, opacity: number) => {
      const watermarkedFile = await applyWatermark(photoId, { opacity });
      if (!watermarkedFile) return;

      setWatermarkOpacities((prev) => {
        const next = new Map(prev);
        next.set(photoId, opacity);
        return next;
      });
      setPhotos((prev) => {
        const updated = prev.map((p) =>
          p.id === photoId ? { ...p, file: watermarkedFile } : p
        );
        onPhotosChange?.(updated);
        return updated;
      });
    },
    [applyWatermark, onPhotosChange]
  );

  const handleWatermarkSizeChange = useCallback(
    async (photoId: number, relativeWidth: number) => {
      const watermarkedFile = await applyWatermark(photoId, { relativeWidth });
      if (!watermarkedFile) return;

      setWatermarkSizes((prev) => {
        const next = new Map(prev);
        next.set(photoId, relativeWidth);
        return next;
      });
      setPhotos((prev) => {
        const updated = prev.map((p) =>
          p.id === photoId ? { ...p, file: watermarkedFile } : p
        );
        onPhotosChange?.(updated);
        return updated;
      });
    },
    [applyWatermark, onPhotosChange]
  );

  /**
   * Helper genérico: aplica uma transformação (rotação ou espelho) sobre
   * a imagem original armazenada ou, quando for foto do servidor (modo edição),
   * carrega diretamente da URI via canvas (crossOrigin=anonymous).
   * Após a transformação, a marca d'água é re-aplicada e a foto passa a
   * ter `.file`, sendo tratada como nova imagem no próximo envio.
   */
  const handleTransformImage = useCallback(
    async (
      photoId: number,
      transformFn: (src: File | string, name: string) => Promise<File>
    ) => {
      const existingOriginal = originalFilesRef.current.get(photoId);
      let source: File | string;

      if (existingOriginal) {
        source = existingOriginal;
      } else {
        // Foto existente no servidor: faz fetch como Blob para evitar o erro
        // "tainted canvas" / CORS que ocorre ao usar crossOrigin='anonymous'
        // quando o servidor não envia os headers Access-Control-Allow-Origin.
        const photo = photos.find((p) => p.id === photoId);
        if (!photo?.uri) return;

        try {
          const response = await fetch(photo.uri, { credentials: 'include' });
          if (!response.ok)
            throw new Error(`HTTP ${response.status}: ${photo.uri}`);
          const blob = await response.blob();
          source = new File([blob], `photo_${photoId}.jpg`, {
            type: blob.type || 'image/jpeg'
          });
        } catch (err) {
          console.error(
            '[useImageUploader] falha ao buscar imagem do servidor:',
            err
          );
          return;
        }
      }

      const transformedFile = await transformFn(source, `photo_${photoId}.jpg`);

      // A imagem transformada vira a nova "original" para re-aplicação de marca
      originalFilesRef.current.set(photoId, transformedFile);

      // Fotos do servidor que foram transformadas passam a ter controles de
      // marca d'água (são tratadas como novas a partir daqui)
      if (!existingOriginal) {
        setWatermarkPositions((prev) => {
          const next = new Map(prev);
          if (!next.has(photoId)) next.set(photoId, DEFAULT_WATERMARK_POSITION);
          return next;
        });
        setWatermarkOpacities((prev) => {
          const next = new Map(prev);
          if (!next.has(photoId)) next.set(photoId, DEFAULT_WATERMARK_OPACITY);
          return next;
        });
        setWatermarkSizes((prev) => {
          const next = new Map(prev);
          if (!next.has(photoId)) next.set(photoId, DEFAULT_WATERMARK_SIZE);
          return next;
        });
      }

      // Usa os valores atuais dos mapas (ou padrões, caso ainda não registrados)
      const position =
        watermarkPositions.get(photoId) ?? DEFAULT_WATERMARK_POSITION;
      const opacity =
        watermarkOpacities.get(photoId) ?? DEFAULT_WATERMARK_OPACITY;
      const relativeWidth =
        watermarkSizes.get(photoId) ?? DEFAULT_WATERMARK_SIZE;

      const watermarkedFile = await applyWatermarkAndConvertToJpeg(
        transformedFile,
        watermarkSrc,
        {
          position,
          centerInQuadrant: DEFAULT_WATERMARK_CENTER_IN_QUADRANT,
          opacity,
          relativeWidth,
          quality: IMAGE_PROCESSING_QUALITY,
          maxWidth: IMAGE_PROCESSING_MAX_WIDTH,
          maxHeight: IMAGE_PROCESSING_MAX_HEIGHT,
          returnFileName: transformedFile.name
        }
      );

      setPhotos((prev) => {
        const updated = prev.map((p) =>
          p.id === photoId ? { ...p, file: watermarkedFile } : p
        );
        onPhotosChange?.(updated);
        return updated;
      });
    },
    [
      photos,
      watermarkPositions,
      watermarkOpacities,
      watermarkSizes,
      onPhotosChange
    ]
  );

  const handleRotateImage = useCallback(
    async (photoId: number, degrees: RotationDegrees) => {
      await handleTransformImage(photoId, (src, name) =>
        rotateImage(src, degrees, {
          quality: IMAGE_PROCESSING_QUALITY,
          returnFileName: name
        })
      );
    },
    [handleTransformImage]
  );

  const handleFlipImage = useCallback(
    async (photoId: number, direction: FlipDirection) => {
      await handleTransformImage(photoId, (src, name) =>
        flipImage(src, direction, {
          quality: IMAGE_PROCESSING_QUALITY,
          returnFileName: name
        })
      );
    },
    [handleTransformImage]
  );

  return {
    photos,
    localImages,
    watermarkPositions,
    watermarkOpacities,
    watermarkSizes,
    setPhotos,
    handleAddImage,
    handleDeleteImage,
    handleSortEnd,
    handleWatermarkPositionChange,
    handleWatermarkOpacityChange,
    handleWatermarkSizeChange,
    handleRotateImage,
    handleFlipImage
  };
};
